'use client'
import React, { useEffect, useRef } from 'react'

import { useWebContainerStore } from '@/lib/webcontainer-store'
import { Loader2, ExternalLink, RefreshCw, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function Preview() {
  const { previewUrl, isBooting, isReady, error } = useWebContainerStore()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handleRefresh = () => {
    if (iframeRef.current && previewUrl) {
      console.log('🔄 Refreshing preview iframe...')
      iframeRef.current.src = previewUrl
    }
  }

  // Auto-refresh disabled to avoid stealing editor focus.

  const handleOpenExternal = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank')
    }
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">Preview</span>
        </div>
        {previewUrl && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleRefresh}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleOpenExternal}>
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      <div className="flex-1 relative">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-destructive/10 p-4">
            <div className="text-center space-y-2">
              <p className="text-destructive font-medium">Error</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : isBooting ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background">
            <div className="text-center space-y-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">Booting WebContainer...</p>
            </div>
          </div>
        ) : !isReady || !previewUrl ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background">
            <div className="text-center space-y-2 p-6">
              <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-foreground">No preview available</p>
              <p className="text-sm text-muted-foreground max-w-xs">
                Add files to your project and click &quot;Run&quot; to start the dev server
              </p>
            </div>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={previewUrl}
            title="Preview"
            className="w-full h-full border-0 bg-white"
            sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
          />
        )}
      </div>
    </div>
  )
}
